import { useState } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Home,
  Music,
  Pause,
  Play,
  Shuffle,
  SkipBack,
  SkipForward,
  Undo2,
  Volume1,
  Volume2,
  VolumeX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { MediaVM } from "@/types/homey";
import { DeviceTile } from "./DeviceTile";
import { DimmerSlider } from "./DimmerSlider";

interface MediaTileProps {
  media: MediaVM;
  /** Toggle play/pause. The component resolves the right capability + value. */
  onPlayPause: () => void;
  onPrev: () => void;
  onNext: () => void;
  /** Set volume_set (0..1). Only called when media.hasVolumeSet. */
  onVolume: (value: number) => void;
  onVolumeUp: () => void;
  onVolumeDown: () => void;
  onMuteToggle: () => void;
  onShuffleToggle: () => void;
  /** D-pad + home/back, for set-top boxes. */
  onDpad: (direction: "up" | "down" | "left" | "right" | "select") => void;
  onHome: () => void;
  onBack: () => void;
  className?: string;
}

/**
 * Media device tile that opens a "now playing" control dialog on tap.
 *
 * Like ClimateTile, the Dialog is CONTROLLED and opened via DeviceTile's own
 * `onToggle` — we deliberately do NOT use `<DialogTrigger asChild><DeviceTile/>`
 * because DeviceTile is not a transparent prop-forwarding primitive (it drops
 * the onClick/aria/ref Radix injects into an asChild trigger). Routing
 * activation through `onToggle` gives keyboard + role handling for free.
 *
 * Controls render conditionally on `media.capabilities`, so the same dialog
 * serves Volumio (volume slider, no D-pad) and an Apple TV (D-pad, volume
 * +/- buttons, no slider). The dialog stays open after actions so users can
 * issue several commands; it reads from the live VM (which re-renders on each
 * snapshot delta) except for the in-flight volume drag handled by DimmerSlider.
 */
export function MediaTile({
  media,
  onPlayPause,
  onPrev,
  onNext,
  onVolume,
  onVolumeUp,
  onVolumeDown,
  onMuteToggle,
  onShuffleToggle,
  onDpad,
  onHome,
  onBack,
  className,
}: MediaTileProps) {
  const [open, setOpen] = useState(false);
  const { capabilities: caps, available } = media;

  const state = !available ? "Offline" : media.playing ? "Playing" : "Paused";

  return (
    <>
      <DeviceTile
        name={media.name}
        icon={<Music />}
        domain="neutral"
        state={available ? media.summary : "Offline"}
        active={media.playing === true && available}
        available={available}
        onToggle={available ? () => setOpen(true) : undefined}
        className={className}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex flex-col gap-5 sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{media.name}</DialogTitle>
          </DialogHeader>

          {/* Artwork — absolute http(s) URL only; otherwise a music-note placeholder.
              We don't proxy homeyimage:// artwork here (would need an authed image
              proxy); that limitation is intentional and noted in toMediaVM. */}
          <div className="grid aspect-square w-full place-items-center overflow-hidden rounded-xl bg-muted">
            {media.artworkUrl ? (
              <img
                src={media.artworkUrl}
                alt=""
                className="h-full w-full object-cover"
                draggable={false}
              />
            ) : (
              <Music className="size-16 text-muted-foreground" aria-hidden />
            )}
          </div>

          {/* Now-playing text */}
          <div className="min-w-0 text-center">
            <p className="truncate text-base font-medium">{media.track ?? state}</p>
            {media.artist && (
              <p className="truncate text-sm text-muted-foreground">{media.artist}</p>
            )}
            {media.album && <p className="truncate text-xs text-muted-foreground">{media.album}</p>}
          </div>

          {/* Transport row: prev / play-pause / next */}
          <div className="flex items-center justify-center gap-3">
            {caps.prev && (
              <Button variant="ghost" size="icon-lg" aria-label="Previous" onClick={onPrev}>
                <SkipBack />
              </Button>
            )}
            {caps.playPause && (
              <Button
                variant="default"
                size="icon-lg"
                aria-label={media.playing ? "Pause" : "Play"}
                onClick={onPlayPause}
              >
                {media.playing ? <Pause /> : <Play />}
              </Button>
            )}
            {caps.next && (
              <Button variant="ghost" size="icon-lg" aria-label="Next" onClick={onNext}>
                <SkipForward />
              </Button>
            )}
            {caps.shuffle && (
              <Button
                variant={media.shuffle ? "secondary" : "ghost"}
                size="icon-lg"
                aria-label="Shuffle"
                aria-pressed={media.shuffle ?? false}
                onClick={onShuffleToggle}
              >
                <Shuffle />
              </Button>
            )}
          </div>

          {/* Volume — slider when volume_set present, else +/- buttons */}
          {(caps.volumeSet || caps.volumeUpDown || caps.mute) && (
            <div className="flex items-center gap-3">
              {caps.mute && (
                <Button
                  variant={media.muted ? "secondary" : "ghost"}
                  size="icon"
                  aria-label={media.muted ? "Unmute" : "Mute"}
                  aria-pressed={media.muted ?? false}
                  onClick={onMuteToggle}
                >
                  {media.muted ? <VolumeX /> : <Volume1 />}
                </Button>
              )}
              {caps.volumeSet ? (
                <DimmerSlider
                  value={media.volume ?? 0}
                  domain="neutral"
                  aria-label="Volume"
                  // Volume mid-drag is owned by DimmerSlider's internal ref; we
                  // only commit on pointer-up to avoid a snapshot rebuild per move.
                  onChange={() => {}}
                  onCommit={onVolume}
                  disabled={!available}
                />
              ) : caps.volumeUpDown ? (
                <div className="flex flex-1 items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Volume down"
                    onClick={onVolumeDown}
                  >
                    <Volume1 />
                  </Button>
                  <Button variant="outline" size="icon" aria-label="Volume up" onClick={onVolumeUp}>
                    <Volume2 />
                  </Button>
                </div>
              ) : null}
            </div>
          )}

          {/* D-pad — set-top boxes (Apple TV) */}
          {caps.dpad && (
            <div className="flex flex-col items-center gap-3">
              <div className="grid grid-cols-3 grid-rows-3 gap-1.5">
                <span />
                <Button variant="outline" size="icon" aria-label="Up" onClick={() => onDpad("up")}>
                  <ChevronUp />
                </Button>
                <span />
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Left"
                  onClick={() => onDpad("left")}
                >
                  <ChevronLeft />
                </Button>
                <Button
                  variant="default"
                  size="icon"
                  aria-label="Select"
                  onClick={() => onDpad("select")}
                >
                  OK
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Right"
                  onClick={() => onDpad("right")}
                >
                  <ChevronRight />
                </Button>
                <span />
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Down"
                  onClick={() => onDpad("down")}
                >
                  <ChevronDown />
                </Button>
                <span />
              </div>
              {(caps.home || caps.back) && (
                <div className="flex items-center justify-center gap-3">
                  {caps.back && (
                    <Button variant="ghost" size="icon" aria-label="Back" onClick={onBack}>
                      <Undo2 />
                    </Button>
                  )}
                  {caps.home && (
                    <Button variant="ghost" size="icon" aria-label="Home" onClick={onHome}>
                      <Home />
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export type { MediaTileProps };
